/**
 * Framework middleware helpers for webhook handling.
 *
 * Provides pre-built integrations for Express/Connect and Next.js App Router.
 */

import { WebhookHandler } from './handler';
import type { WebhookHandlerConfig, WebhookEventType, WebhookEventHandler, WebhookAnyHandler } from './types';

export interface WebhookMiddlewareOptions extends WebhookHandlerConfig {
  /** Event handlers to register */
  handlers?: Partial<Record<WebhookEventType, WebhookEventHandler>>;
  /** Catch-all handler */
  onAny?: WebhookAnyHandler;
}

/**
 * Create an Express/Connect-compatible middleware for webhook handling.
 *
 * Expects the raw body to be available as `req.body` (string).
 * Use `express.raw({ type: 'application/json' })` or similar middleware upstream.
 */
export function createWebhookMiddleware(options: WebhookMiddlewareOptions) {
  const handler = buildHandler(options);
  const signatureHeader = options.signatureHeader || 'x-webhook-signature';

  return async (
    req: { body: string | Buffer; headers: Record<string, string | string[] | undefined> },
    res: { status(code: number): { json(body: unknown): void }; json?(body: unknown): void },
    next?: (err?: unknown) => void
  ) => {
    try {
      const body = typeof req.body === 'string' ? req.body : req.body.toString('utf-8');
      const signature = req.headers[signatureHeader];

      if (!signature || typeof signature !== 'string') {
        res.status(401).json({ error: 'Missing webhook signature' });
        return;
      }

      await handler.handle(body, signature);
      res.status(200).json({ received: true });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Webhook processing failed';

      if (message.includes('signature')) {
        res.status(401).json({ error: message });
      } else if (message.includes('tolerance') || message.includes('timestamp')) {
        res.status(400).json({ error: message });
      } else if (next) {
        next(error);
      } else {
        res.status(500).json({ error: message });
      }
    }
  };
}

/**
 * Create a Next.js App Router-compatible webhook handler.
 *
 * Returns an async function `(request: Request) => Promise<Response>`.
 */
export function createNextWebhookHandler(
  options: WebhookMiddlewareOptions
): (request: Request) => Promise<Response> {
  const handler = buildHandler(options);
  const signatureHeader = options.signatureHeader || 'x-webhook-signature';

  return async (request: Request): Promise<Response> => {
    try {
      const body = await request.text();
      const signature = request.headers.get(signatureHeader);

      if (!signature) {
        return new Response(JSON.stringify({ error: 'Missing webhook signature' }), {
          status: 401,
          headers: { 'Content-Type': 'application/json' },
        });
      }

      await handler.handle(body, signature);

      return new Response(JSON.stringify({ received: true }), {
        status: 200,
        headers: { 'Content-Type': 'application/json' },
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Webhook processing failed';
      const status = message.includes('signature')
        ? 401
        : message.includes('tolerance') || message.includes('timestamp')
          ? 400
          : 500;

      return new Response(JSON.stringify({ error: message }), {
        status,
        headers: { 'Content-Type': 'application/json' },
      });
    }
  };
}

function buildHandler(options: WebhookMiddlewareOptions): WebhookHandler {
  const handler = new WebhookHandler(options);

  if (options.handlers) {
    for (const [eventType, eventHandler] of Object.entries(options.handlers)) {
      if (eventHandler) {
        handler.on(eventType as WebhookEventType, eventHandler);
      }
    }
  }

  if (options.onAny) {
    handler.onAny(options.onAny);
  }

  return handler;
}
